import React, { useState } from 'react';
import useIcons from '../../assets/icons/useIcons';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../hooks/useAlert';
import Spinner from '../Spinner';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

const TableRow = ({
  row,
  enableMoreOption,
  showMoreOption,
  flexRender,
  rowSelection,
  moreDataFunction = () => null,
  fetcherKey = '',
  moreDataFnPayload,
  memoizedColumns,
}) => {
  const shouldHighlight =
    row?.original?.hasOwnProperty('seen_at') &&
    row?.original?.hasOwnProperty('recording_file')
      ? !row?.original?.seen_at && Boolean(row?.original?.recording_file)
        ? true
        : false
      : false;

  const { AddIcon, MinusIcon } = useIcons();
  const { showAlert } = useAlert();

  const [relevantData, setRelevantData] = useState([]);
  const [showRelevantData, setShowRelevantData] = useState(false);

  const { isLoading: loadingMoreData, mutate: loadMoreDataFn } = useMutation({
    mutationFn: moreDataFunction,
    mutationKey: [`${fetcherKey}`],
    onError: (error) => {
      const errMsg = error?.message ?? 'unexpected error';
      showAlert({ type: 'secondary', message: errMsg });
    },
    onSuccess: (data) => {
      setRelevantData(data?.data?.data || []);
    },
  });

  function handleMoreData() {
    const payload = moreDataFnPayload(row?.original);
    loadMoreDataFn(payload);
  }

  const tableInstance = useReactTable({
    debugAll: false,
    data: relevantData || [],
    columns: memoizedColumns || [],
    columnResizeMode: 'onChange',
    state: {
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <tr
        key={row?.id}
        className={`border--bottom--primary-xs border--bottom--primary-xs--2 ${
          rowSelection?.[row?.index] ? 'selected' : ''
        } ${shouldHighlight ? 'font--600' : ''}`}
      >
        {enableMoreOption && (
          <td
            style={{
              width: 'fit-content',
              padding: '0px',
            }}
          >
            {showMoreOption(row?.original) && (
              <>
                {loadingMoreData ? (
                  <div className="d--flex w-max--16 w-min--16 h-min--16 h-max--16 m--sm">
                    <Spinner size="xs" />
                  </div>
                ) : (
                  <div
                    className="c--pointer w-max--16 w-min--16 h-min--16 radius--xs bg--primary d--flex align-items--center justify-content--center text--black m--sm"
                    onClick={() => {
                      if (relevantData?.length > 0) {
                        setShowRelevantData((_) => !_);
                      } else {
                        handleMoreData();
                        setShowRelevantData(true);
                      }
                    }}
                  >
                    {showRelevantData && relevantData?.length > 0 ? (
                      <MinusIcon width={10} height={10} />
                    ) : (
                      <AddIcon width={10} height={10} />
                    )}
                  </div>
                )}
              </>
            )}
          </td>
        )}
        {row?.getVisibleCells()?.map((cell) => {
          return (
            <td
              className="d--flex-xs flex--column-xs justify-content--center-xs w--full-xs"
              key={cell?.id}
              style={{
                textAlign: cell?.column?.columnDef?.meta?.textAlign ?? 'left',
                width:
                  cell?.column?.columnDef?.meta?.width ||
                  `${(100 / cell?.headerGroup?.headers?.length).toFixed(2)}%`,
                cursor: cell?.column?.columnDef?.meta?.shouldPointer
                  ? 'pointer'
                  : '',
              }}
            >
              <div className="d--flex-xs align-items--center-xs  justify-content--between-xs  border--0 w--full-xs">
                <span className="d--flex-xs font--600 d--none">
                  {flexRender(cell.column.columnDef.header, cell.getContext())}
                </span>
                {cell?.column?.id === 'action' ||
                cell?.column?.columnDef?.meta?.colType === 'action'
                  ? flexRender(cell.column.columnDef.cell, cell.getContext())
                  : cell?.getValue() || parseInt(cell?.getValue()) === 0
                  ? flexRender(cell.column.columnDef.cell, cell.getContext())
                  : '---'}
              </div>
            </td>
          );
        })}
      </tr>

      {showRelevantData && (
        <>
          {tableInstance?.getRowModel()?.rows &&
            tableInstance?.getRowModel()?.rows.length > 0 &&
            tableInstance?.getRowModel()?.rows?.map((row) => {
              return (
                <tr
                  key={row?.id}
                  className={`${rowSelection?.[row?.index] ? 'selected' : ''}`}
                >
                  <td
                    className="bg--black-50"
                    style={{
                      width: '1%',
                    }}
                  ></td>
                  {row?.getVisibleCells()?.map((cell) => {
                    return (
                      <td
                        className="bg--black-50"
                        key={cell?.id}
                        style={{
                          textAlign:
                            cell?.column?.columnDef?.meta?.textAlign ?? 'left',
                          width:
                            cell?.column?.columnDef?.meta?.width ||
                            `${(
                              100 / cell?.headerGroup?.headers?.length
                            ).toFixed(2)}%`,
                          cursor: cell?.column?.columnDef?.meta?.shouldPointer
                            ? 'pointer'
                            : '',
                        }}
                      >
                        <>
                          {cell?.column?.id === 'action' ||
                          cell?.column?.columnDef?.meta?.colType === 'action'
                            ? flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )
                            : cell?.getValue() ||
                              parseInt(cell?.getValue()) === 0
                            ? flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )
                            : '---'}
                        </>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </>
      )}
    </>
  );
};

export default TableRow;
